export interface ProcessStep {
  number: string;
  title: string;
  description: string;
}

const STEP_ICONS = [
  "ri-calendar-check-line",
  "ri-draft-line",
  "ri-building-4-line",
  "ri-hammer-line",
  "ri-award-line",
];

interface ProcessSectionProps {
  title: string;
  subtitle?: string;
  steps: ProcessStep[];
}

export default function ProcessSection({ title, subtitle, steps }: ProcessSectionProps) {
  return (
    <section className="bg-aegisCream py-28 overflow-hidden relative">
      {/* Large decorative label */}
      <span className="absolute right-0 top-1/2 -translate-y-1/2 font-cormorant font-semibold text-[200px] text-aegisDark/5 leading-none select-none pointer-events-none hidden xl:block" style={{ lineHeight: 1 }}>
        BUILD
      </span>

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end mb-20">
          <div>
            <div className="flex items-center gap-4 mb-5">
              <div className="h-px w-10 bg-aegisGold" />
              <span className="font-inter text-xs tracking-[0.2em] uppercase text-aegisGold">How It Works</span>
            </div>
            <h2 className="font-cormorant font-semibold text-aegisDark leading-[1.1]" style={{ fontSize: "clamp(2.2rem, 3.5vw, 3.4rem)" }}>
              {title}
            </h2>
          </div>
          {subtitle && (
            <p className="font-inter text-aegisMuted text-base leading-relaxed lg:pl-8 lg:border-l lg:border-aegisStone">
              {subtitle}
            </p>
          )}
        </div>

        {/* Steps grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-aegisStone relative z-10">
          {steps.map((step, idx) => (
            <div key={step.number} className="group bg-aegisCream hover:bg-white transition-colors duration-300 p-8 flex flex-col relative">
              {/* Step number — decorative */}
              <div className="mb-8">
                <span className="font-cormorant font-semibold text-7xl leading-none text-aegisGold/15 group-hover:text-aegisGold/25 transition-colors duration-300 select-none block">
                  {step.number}
                </span>
              </div>

              {/* Icon */}
              <div className="w-12 h-12 flex items-center justify-center border-2 border-aegisGold/30 group-hover:border-aegisGold bg-white transition-colors duration-300 mb-6">
                <i className={`${STEP_ICONS[idx] || "ri-settings-line"} text-aegisGold text-xl`} />
              </div>

              {/* Step label */}
              <span className="font-inter text-xs tracking-[0.18em] uppercase text-aegisGold mb-2 block">
                Step {step.number}
              </span>
              <h3 className="font-cormorant font-semibold text-aegisDark text-xl mb-4 leading-tight">
                {step.title}
              </h3>
              <p className="font-inter text-aegisMuted text-sm leading-relaxed flex-1">
                {step.description}
              </p>

              {/* Connector dot */}
              {idx < steps.length - 1 && (
                <div className="hidden lg:flex absolute top-10 -right-3 w-6 h-6 items-center justify-center z-10">
                  <div className="w-2 h-2 bg-aegisGold rotate-45" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom gold bar */}
        <div className="mt-0 h-1 bg-gradient-to-r from-aegisGold via-aegisGold/50 to-transparent" />
      </div>
    </section>
  );
}
